import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  BehaviorSubject,
  finalize,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AccessRequestsService } from '../../api/access-requests.service';
import { AdminAccessRequestsService } from '../../api/admin-access-requests.service';
import { AuthService } from '@auth0/auth0-angular';
import { LandingSection } from '../../models/landing-section.model';
import { LandingModel } from '../../models/laniding.model';
import { LandingService } from '../../services/landing.service';

@Component({
  selector: 'of-landing-page',
  standalone: true,
  imports: [CommonModule, RouterModule, MatProgressSpinnerModule],
  template: `

  <ng-container *ngIf="vm$ | async as vm;">
    <header class="hero">
      <div class="hero-overlay" aria-hidden="true"></div>
      <div class="hero-content">
        <div class="hero-logo">
          <img
            class="hero-logo__img"
            src="/assets/logo-trans.png"
            alt="Omniscient Forge logo"
            width="200"
            height="200"
            decoding="async"
          />
          <span>
            OMNISCIENT <span class="forge-text">FORGE</span>
          </span>
        </div>
        <h1>Build worlds by story beats, not stat blocks.</h1>
        <p>
          Omniscient Forge is a lore-first engine for crafting worlds built on relationships,
          secrets, and the people who carry them.
        </p>
        <p class="hero-beta">
          Omniscient Forge is currently in closed beta. Request access to the open beta by signing
          in below.
        </p>

        <button
          type="button"
          class="btn-primary-gradient"
          (click)="primaryAction(vm)"
          [disabled]="vm.primaryDisabled"
          [attr.aria-disabled]="vm.primaryDisabled"
        >
          {{ vm.primaryLabel }}
        </button>
        <p *ngIf="vm.showAccessNote" class="support-text hero-access-note">
          You are signed in. Workspace access unlocks after global admin approval.
        </p>
      </div>
    </header>

    <section class="landing-sections">
      <div class="landing-grid">
        <article
          *ngFor="let section of sections; index as i"
          class="landing-panel"
          [attr.data-tone]="section.tone"
          [style.--delay]="i * 120 + 'ms'"
        >
          <p class="landing-panel__kicker">{{ section.kicker }}</p>
          <h2>{{ section.title }}</h2>
          <p>{{ section.body }}</p>
          <ul class="landing-panel__list">
            <li *ngFor="let bullet of section.bullets">{{ bullet }}</li>
          </ul>
        </article>
      </div>
    </section>
  </ng-container>
  `,
})
export class LandingPage {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  readonly vm$ = this.refresh$.pipe(
    startWith(void 0), // load once on page enter
    switchMap(() => this.landingService.getLandingActions()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly sections: LandingSection[] = [
    {
      kicker: 'Overview',
      title: 'What is Omniscient Forge',
      body:
        'Omniscient Forge is a lore-first worldbuilding engine that turns your notes into a connected story atlas. Capture the people, places, and powers of your setting, then let the relationships tell you what happens next.',
      bullets: [
        'Relationship-driven entity model',
        'Consistent layouts for fast scanning',
        'GM-only notes and visibility controls',
      ],
      tone: 'ember',
    },
    {
      kicker: 'Workflow',
      title: 'How it Works',
      body:
        'Start with a world, add regions and points of interest, then connect factions, NPCs, and quests as living threads. Every detail links to another, so story beats surface naturally and stay easy to update.',
      bullets: ['Create -> connect -> enrich', 'Sortable, paginated list views', 'Save-state cues prevent lost edits'],
      tone: 'cyan',
    },
    {
      kicker: 'Integration',
      title: 'Foundry Module & Integration',
      body:
        'Sync your world to Foundry VTT with the Omniscient Forge module. Bring in maps, handouts, and structured lore without re-entering data, and keep updates flowing as your story evolves.',
      bullets: ['Credential Manager handles API keys', 'Foundry IDs on synced entities', 'Media links stay attached'],
      tone: 'slate',
    },
  ];

  constructor(private readonly router: Router,
    private readonly auth: AuthService,
    private readonly accessRequests: AccessRequestsService,
    private readonly adminAccessRequests: AdminAccessRequestsService,
    private readonly landingService: LandingService) {

  }

  primaryAction(vm: LandingModel) {
    if (!vm.isAuthenticated) {
      console.log("IS not authenticated");
      this.auth.loginWithRedirect();
      return;
    }

    if (vm.canAccessWorkspace && !vm.primaryDisabled) {
      void this.router.navigate(['/workspace']);
      return;
    }

    if (vm.canRequestAccess && !vm.primaryDisabled) {
      this.accessRequests.requestAccess()
        .pipe(finalize(() => this.refresh$.next()))
        .subscribe();
    }
  }
}
